<template>
  <div>
    <p
      v-if="label"
      class="mb-4 text-lg uppercase"
    >
      {{ label }}
    </p>

    <component :is="calculatorType" />

    <div class="text-center">
      <atoms-button
        v-for="(button, buttonKey) in buttons"
        :id="button?.id ?? `button_${generateUID()}`"
        :key="buttonKey"
        :theme="button.theme || 'primary'"
        :to="button.url"
        :class="button?.class ?? ''"
        :cross-app="button.crossApp ?? false"
        size="large"
        v-on="{
          ...((button?.callback && {
            click: () => button.callback?.()
          }) || {})
        }"
      >
        {{ button.text }}
      </atoms-button>
    </div>

    <atoms-alert
      v-if="!_isEmpty(disclaimerList)"
      size="p2"
      type="disclaimer"
      full-width
      class="mt-6 md:mt-10"
    >
      <!-- eslint-disable vue/no-v-html -->
      <p
        v-for="(item, itemKey) in disclaimerList"
        :key="itemKey"
        class="mb-6 last:mb-0"
        v-html="item"
      >
      </p>
    </atoms-alert>

    <div
      v-if="staticPageData?.mainContent"
      class="mt-4"
    >
      <RichTextRenderer
        :document="staticPageData?.mainContent?.json"
      />
    </div>
  </div>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'
import MoleculesCalculatorRefinance from '../Refinance'
import MoleculesCalculatorBorrowingPower from '../BorrowingPower'
import MoleculesCalculatorStampDuty from '../StampDuty'
import MoleculesCalculatorDeposit from '../Deposit'
import MoleculesCalculatorBudgetPlanner from '../BudgetPlanner'
import MoleculesCalculatorBuyingCosts from '../BuyingCosts'
import MoleculesCalculatorSellingCosts from '../SellingCosts'
import { getKey, strToCapitalize, strToCamelCase } from '~/helpers/functions.js'
import { CALCULATORS, CALCULATOR_DISCLAIMER } from '~/constants/homeloans.constant'
import RichTextRenderer from 'contentful-rich-text-vue-renderer'
import StaticPageService from '~/server/contentful/static-page.service'

defineOptions({
  name: 'MoleculesCalculatorWithDisclaimer'
})

const $route = useRoute()
const staticPageService = new StaticPageService()
const slug = $route.path.replace(/\/$/, '')
const staticPageServiceResponse = await staticPageService.getStaticPage({ slug })

const props = defineProps({
  label: {
    type: String,
    default: ''
  },

  calculator: {
    type: String,
    default: '',
    validator: type => CALCULATORS.map(item => strToCamelCase(item)).includes(type),
    required: true
  },

  disclaimer: {
    type: Array,
    default: () => []
  },

  buttons: {
    type: Array,
    default: () => ([]),
    validator: arr => {
      if (arr.length === 0) {
        return true
      }

      arr.forEach(item => {
        if (!item.text) {
          return false
        }

        if (
          !item.theme ||
          ['primary', 'secondary', 'tertiary', 'link'].includes(item.theme)
        ) {
          return false
        }
      })

      return true
    }
  }
})

const staticPageData = computed(() => staticPageServiceResponse)

const calculatorType = computed(() => {
  const calculatorMap = {
    Refinance: MoleculesCalculatorRefinance,
    BorrowingPower: MoleculesCalculatorBorrowingPower,
    StampDuty: MoleculesCalculatorStampDuty,
    Deposit: MoleculesCalculatorDeposit,
    BudgetPlanner: MoleculesCalculatorBudgetPlanner,
    BuyingCosts: MoleculesCalculatorBuyingCosts,
    SellingCosts: MoleculesCalculatorSellingCosts
  }

  const capitalizedCalculator = strToCapitalize(props.calculator)
  return calculatorMap[capitalizedCalculator] || `MoleculesCalculator${capitalizedCalculator}`
})

const disclaimerList = computed(() => {
  const disclaimer = _isEmpty(props.disclaimer)
    ? getKey(props.calculator, CALCULATOR_DISCLAIMER)
    : props.disclaimer

  return Array.isArray(disclaimer)
    ? disclaimer
    : [disclaimer]
})
</script>
